import {
  type CampaignMetrics,
  type CampaignOverviewFilterTargetEnum,
  type CampaignStats,
} from '@metapic-apps/shared'
import { computed, type Ref, ref, watch } from 'vue'

import {
  FINANCE_CAMPAIGN_METRICS,
  getFinanceChartMetric,
  normalizeFinanceCampaignDailyStats,
  normalizeFinanceCampaignSummary,
} from '@/lib/adapters/campaign-finance-performance.adapter'
import { useApi } from '@/lib/helpers/api.helpers'
import { financeClient } from '@/lib/services/finance.service'

type DateRange = { startDate: string; endDate: string }

export const useFinanceCampaignPerformance = (
  advertiserId: number,
  campaignId: number,
  activeFilterTarget: Ref<CampaignOverviewFilterTargetEnum>,
  dateRange: Ref<DateRange>,
): {
  data: Ref<CampaignStats | undefined>
  isLoading: Ref<boolean>
  isChartLoading: Ref<boolean>
} => {
  const summary = ref<CampaignMetrics>()
  const dailyStats = ref<CampaignStats['dailyStats']>([])

  const { loading: isSummaryLoading, fetchSummary } = useApi({
    fetchSummary: async ({ startDate, endDate }: DateRange) => {
      const response = await financeClient.performanceControllerGetSummary({
        advertiser_id: advertiserId,
        campaign_id: campaignId,
        date_from: startDate,
        date_to: endDate,
        metrics: FINANCE_CAMPAIGN_METRICS,
      })
      summary.value = normalizeFinanceCampaignSummary(response.metrics)
    },
  })

  const { loading: isChartLoading, fetchChart } = useApi({
    fetchChart: async () => {
      const metric = getFinanceChartMetric(activeFilterTarget.value)
      if (!metric) {
        dailyStats.value = []
        return
      }
      const { startDate, endDate } = dateRange.value
      const response = await financeClient.performanceControllerGetChart({
        advertiser_id: advertiserId,
        campaign_id: campaignId,
        date_from: startDate,
        date_to: endDate,
        metric,
        breakdown_by: 'traffic_source',
      })
      dailyStats.value = normalizeFinanceCampaignDailyStats(response, metric)
    },
  })

  watch(dateRange, () => void fetchSummary(dateRange.value), { immediate: true, deep: true })
  watch([activeFilterTarget, dateRange], () => void fetchChart(), { immediate: true, deep: true })

  const data = computed<CampaignStats | undefined>(() =>
    summary.value ? { summary: summary.value, dailyStats: dailyStats.value } : undefined,
  )

  return {
    data,
    isLoading: isSummaryLoading,
    isChartLoading,
  }
}
